/**
 * PLUMBING PLAZA — water-works square realism pass (2026-11-04, owner:
 * "improve each map", "realistic", "get inspired by existing models, don't
 * copy them").
 *
 * The map is the second Chibre Arena layout: six square "pipe" columns
 * around a central fountain plinth inside a 16 m perimeter wall — originally
 * cartoon warp pipes, which the renderer now dresses as small buildings.
 * Made a believable place: a municipal water-works square. Each block gets a
 * painted cast-iron rising main (flanges, ground elbow, red hand wheel,
 * pressure gauge, enamel "SERVICE DES EAUX" plaque), the fountain plinth
 * becomes a cut-stone basin
 * with a coping and a shallow reflecting pool around a bronze spout, and the
 * slab becomes a flagstone square with granite aprons and banding, manholes
 * and drains. Inspiration (not copies): Victorian pumping stations,
 * Paris Wallace fountains and standpipes, cast-iron water-tower photography.
 *
 * Everything sits on/inside the shared colliders (cityDressing.ts contract,
 * checked by agent/bench-extras-chibre.mjs). ≤ 9 draw calls, built once.
 */
import type * as THREE from "three";
import type { MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { ATLAS, buildPieces, dressFacade, floorCells, h01, Scene, streetClutter, type Face, type FacadeStyle, type Piece } from "../cityDressing.js";
export { WALL_PROUD, GROUND_LIFT, TOP_LIFT } from "../cityDressing.js";

export const mapIds = ["plumbing_plaza"];

const C = {
  flag: 0x8f887c, flag2: 0x847d71, granite: 0x8d8a85, granite2: 0x7f7c77,
  iron: 0x3c5546, rivet: 0x2f4437,
  basin: 0xa9a08f, basin2: 0x9d9483, coping: 0xbdb4a3, bronze: 0x7a5c36, gaugeBack: 0x2c2f31,
};
const STYLE: FacadeStyle = {
  plinth: 0x8a8174, plinth2: 0x81796c, cornice: 0xd2c8b6, cornice2: 0xc3b9a7, string: 0xc9bfad,
  pipe: 0x3a3e42, lamp: 0x2a2c2e, plaque: null, lanterns: true,
};
const APRON = 1.6;

export function cityLayout(map: MapDef, lowSpec: boolean): Piece[] {
  const S = new Scene(map);
  const boxes = map.boxes;
  const A = S.A;
  const onSlab = (i: number): boolean => !S.floors.has(i) && A[i].y0 < 0.5 && !S.isWall(i);
  // a column's collar ring: a 0.5 m box wider than the column, near its top
  const ringOf = new Map<number, number>();
  boxes.forEach((b, i) => {
    if (!onSlab(i)) return;
    const j = boxes.findIndex((r, k) => k !== i && Math.abs(r.s[1] - 0.5) < 0.01 && Math.abs(r.p[0] - b.p[0]) < 0.01 && Math.abs(r.p[2] - b.p[2]) < 0.01 && r.s[0] > b.s[0]);
    if (j >= 0) ringOf.set(i, j);
  });
  const columns = [...ringOf.keys()];
  const fountain = boxes.findIndex((b, i) => onSlab(i) && !ringOf.has(i));
  const spout = fountain < 0 ? -1 : boxes.findIndex((b, i) => i !== fountain && Math.abs(b.p[0] - boxes[fountain].p[0]) < 0.01 && Math.abs(b.p[2] - boxes[fountain].p[2]) < 0.01 && A[i].y0 >= A[fountain].y1 - 0.01);

  // ── GROUND: flagstones, granite aprons round every structure + the walls ──
  floorCells(S, APRON, (i) => onSlab(i) || S.isWall(i), (x0, x1, z0, z1, near) => {
    if (near) { S.ground("stone", "apron", x0, x1, z0, z1, 0.04, h01(Math.round(x0 * 5), Math.round(z0 * 5), 4) < 0.5 ? C.granite : C.granite2, { tile: 1.2 }); return; }
    // a 3 m checker of two flag tones inside each open cell
    const xs = [x0], zs = [z0];
    for (let x = Math.ceil(x0 / 3) * 3; x < x1 - 0.01; x += 3) if (x > x0 + 0.01) xs.push(x);
    for (let z = Math.ceil(z0 / 3) * 3; z < z1 - 0.01; z += 3) if (z > z0 + 0.01) zs.push(z);
    xs.push(x1); zs.push(z1);
    for (let a = 0; a < xs.length - 1; a++) for (let b = 0; b < zs.length - 1; b++) {
      const cx = Math.floor((xs[a] + xs[a + 1]) / 6), cz = Math.floor((zs[b] + zs[b + 1]) / 6);
      S.ground("pave", "flags", xs[a], xs[a + 1], zs[b], zs[b + 1], 0.03, (cx + cz) & 1 ? C.flag : C.flag2, { tile: 1.8 });
    }
  });
  // granite banding every 9 m across the square, broken where anything stands
  const P = S.play;
  for (let v = Math.ceil(P.x0 / 9) * 9; v < P.x1; v += 9) {
    for (let z = P.z0 + APRON; z < P.z1 - APRON - 1; z += 1.5) S.ground("stone", "band", v - 0.2, v + 0.2, z + 0.01, z + 1.49, 0.042, C.granite, { tile: 1.2 });
  }
  for (let v = Math.ceil(P.z0 / 9) * 9; v < P.z1; v += 9) {
    for (let x = P.x0 + APRON; x < P.x1 - APRON - 1; x += 1.5) {
      // skip crossings already laid by the x bands
      const cx = x + 0.75;
      if (Math.abs(cx - Math.round(cx / 9) * 9) < 0.95) continue;
      S.ground("stone", "band", x + 0.01, x + 1.49, v - 0.2, v + 0.2, 0.042, C.granite, { tile: 1.2 });
    }
  }
  streetClutter(S, lowSpec ? 8 : 18, APRON + 0.3, 57, ["manhole", "grate", "crack", "manhole"]);

  // ── PERIMETER: the square's surrounding facades ───────────────────────
  boxes.forEach((_, i) => { if (S.isWall(i)) for (const f of S.faces(i)) dressFacade(S, f, STYLE, i, lowSpec); });

  // ── COLUMN BLOCKS: the renderer already dresses these as small buildings
  // (windows, shopfronts, signs). This pass only adds the water-works
  // plumbing a square like this shows on its walls: one cast-iron rising main
  // with flanges + a hand wheel per block (on its widest free face), and the
  // enamel "SERVICE DES EAUX" plaque.
  for (const i of columns) {
    const faces = S.faces(i).sort((p, q) => (q.t1 - q.t0) - (p.t1 - p.t0) || p.sign - q.sign);
    for (const f of faces) if (risingMain(S, f, A[ringOf.get(i)!].y0, i, lowSpec)) break;
  }

  // ── FOUNTAIN: cut-stone basin, coping, reflecting pool, bronze spout ──
  if (fountain >= 0) {
    const a = A[fountain];
    for (const f of S.faces(fountain)) {
      const W = f.t1 - f.t0, tm = (f.t0 + f.t1) / 2, top = f.y1;
      // ashlar courses (alternating tones), plinth, mid moulding, coping
      const course = 0.45;
      for (let k = 0, y = 0.3; y < top - 0.35; k++, y += course) {
        const h = Math.min(course, top - 0.35 - y);
        const n = Math.max(1, Math.round(W / 1.1));
        const off = (k % 2) * 0.5;
        for (let j = 0; j <= n; j++) {
          const t0 = f.t0 + (W * Math.max(0, j - off)) / n, t1 = f.t0 + (W * Math.min(n, j + 1 - off)) / n;
          if (t1 - t0 < 0.1) continue;
          S.onFace(f, "stone", "ashlar", (t0 + t1) / 2, y + h / 2, t1 - t0 - 0.025, h - 0.025, 0.03, h01(j, k, fountain) < 0.5 ? C.basin : C.basin2, { tile: 1.1 });
        }
      }
      S.onFace(f, "stone", "plinth", tm, 0.15, W, 0.3, 0.12, 0x9d968a, { tile: 1.2 });
      S.onFace(f, "stone", "coping", tm, top - 0.17, W, 0.34, 0.2, C.coping, { tile: 1.2 });
      S.onFace(f, "stone", "moulding", tm, top - 0.42, W, 0.1, 0.1, C.coping, { tile: 1.2 });
      // a bronze lion-mask spout plate centred on each side
      S.onFace(f, "copper", "mask", tm, top - 1.1, 0.5, 0.55, 0.08, C.bronze);
      S.onFace(f, "copper", "maskSpout", tm, top - 1.25, 0.12, 0.12, 0.22, C.bronze, { shape: "cyl" });
    }
    // top: stone rim frame + water inside it (+ the spout keeps its own box)
    const m = 0.55;
    S.top(fountain, "stone", "rim", a.x0, a.x1, a.z0, a.z0 + m, 0.06, C.coping, { tile: 1.2 });
    S.top(fountain, "stone", "rim", a.x0, a.x1, a.z1 - m, a.z1, 0.06, C.coping, { tile: 1.2 });
    S.top(fountain, "stone", "rim", a.x0, a.x0 + m, a.z0 + m, a.z1 - m, 0.06, C.coping, { tile: 1.2 });
    S.top(fountain, "stone", "rim", a.x1 - m, a.x1, a.z0 + m, a.z1 - m, 0.06, C.coping, { tile: 1.2 });
    // pool floor (dark, wet stone) then the water surface above it, in four
    // strips round the spout (nothing may sit under the spout collider)
    const sp = spout >= 0 ? A[spout] : null;
    const strips: [number, number, number, number][] = sp
      ? [[a.x0 + m, a.x1 - m, a.z0 + m, sp.z0], [a.x0 + m, a.x1 - m, sp.z1, a.z1 - m], [a.x0 + m, sp.x0, sp.z0, sp.z1], [sp.x1, a.x1 - m, sp.z0, sp.z1]]
      : [[a.x0 + m, a.x1 - m, a.z0 + m, a.z1 - m]];
    for (const [x0, x1, z0, z1] of strips) {
      S.top(fountain, "stone", "poolBed", x0, x1, z0, z1, 0.008, 0x5f6461, { tile: 1.2 });
      S.top(fountain, "water", "pool", x0, x1, z0, z1, 0.045, 0xffffff, { quad: true });
    }
    if (spout >= 0) {
      for (const f of S.faces(spout)) {
        const W = f.t1 - f.t0, tm = (f.t0 + f.t1) / 2;
        S.onFace(f, "copper", "spoutClad", tm, (f.y0 + f.y1) / 2, W, f.y1 - f.y0, 0.012, C.bronze, { shape: "quad" });
        for (const y of [f.y0 + 0.18, f.y0 + 1.2, f.y1 - 0.2]) S.onFace(f, "copper", "spoutBand", tm, y, W, 0.12, 0.08, 0x6f5532);
        S.onFace(f, "copper", "spoutNozzle", tm, f.y1 - 0.55, 0.16, 0.16, 0.24, 0x6f5532, { shape: "cyl" });
      }
      const s = A[spout];
      S.top(spout, "copper", "spoutCap", s.x0 + 0.1, s.x1 - 0.1, s.z0 + 0.1, s.z1 - 0.1, 0.06, 0x6f5532);
    }
  }
  return S.out;
}

/** A rising main up one face near a corner (where shopfronts are not):
 *  flanged pipe, ground elbow, hand wheel, pressure gauge, works plaque. */
function risingMain(S: Scene, f: Face, ringY0: number, host: number, lowSpec: boolean): boolean {
  const side = h01(host, f.axis === "x" ? 1 : 2, 5) < 0.5 ? -1 : 1;
  const pt = side < 0 ? f.t0 + 0.75 : f.t1 - 0.75;
  const y1 = ringY0 - 0.05;
  if (y1 < 2) return false;
  if (!S.onFace(f, "metal", "main", pt, y1 / 2, 0.26, y1, 0.25, C.iron, { shape: "cyl" })) return false;
  for (let y = 0.6; y < y1 - 0.2; y += 1.4) S.onFace(f, "metal", "flange", pt, y, 0.36, 0.07, 0.26, C.rivet);
  S.onFace(f, "metal", "elbowFoot", pt, 0.2, 0.46, 0.4, 0.26, C.rivet);
  const wt = pt - side * 0.62, wy = 1.3;
  S.onFace(f, "metal", "valveBody", wt, wy, 0.2, 0.2, 0.1, C.rivet);
  S.onFace(f, "metal", "wheelSpoke", wt, wy, 0.5, 0.035, 0.18, 0xa1221a);
  S.onFace(f, "metal", "wheelSpoke", wt, wy, 0.035, 0.5, 0.18, 0xa1221a);
  for (const [dt, dy, w, h] of [[0, 0.24, 0.52, 0.04], [0, -0.24, 0.52, 0.04], [0.24, 0, 0.04, 0.52], [-0.24, 0, 0.04, 0.52]] as const) {
    S.onFace(f, "metal", "wheelRim", wt + dt, wy + dy, w, h, 0.2, 0xa1221a);
  }
  if (!lowSpec && S.onFace(f, "metal", "gaugeBack", pt - side * 0.62, 2.0, 0.32, 0.32, 0.07, C.gaugeBack)) {
    S.onFace(f, "paint", "gauge", pt - side * 0.62, 2.0, 0.28, 0.28, 0.075, 0xffffff, { shape: "quad", uv: [...ATLAS.gauge] as [number, number, number, number] });
  }
  S.onFace(f, "paint", "plaque", pt - side * 1.9, 2.15, 1.4, 0.7, 0.02, 0xffffff, { shape: "quad", uv: [...ATLAS.plaqueEaux] as [number, number, number, number] });
  return true;
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const pieces = cityLayout(ctx.map, ctx.lowSpec);
  if (!pieces.length) return null;
  return buildPieces(pieces, "extras:plumbing_plaza", ctx.theme, ctx.shadows, ctx.lowSpec);
}
